import { isSuccess, type Result } from '@carvana/merch-result';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback } from 'react';
import { httpWithAuth } from '~/clients';
import { FinancingApiClientId } from '~/constants/common/ApiClientId';
import { toggleFinancingCoApplicant } from '~/constants/endpoints';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import { useFinancingContext } from '~/context/flp/FinancingContext';
import type { CreditApplication, PricingAggregates } from '~/contracts/flp';
import ErrorWithContext from '~/utilities/ErrorWithContext';
import showApiError from '~/utilities/flp/showApiError';
import { logError } from '~/utilities/loggers';
import toError from '~/utilities/toError';

export default function useToggleCoSigner() {
  const { tradeIn, setCoSigner, setIsLoading, setRefreshedData } =
    useFinancingContext();
  const eventCorrelationId = useEventCorrelationId();

  return useCallback(async () => {
    setIsLoading(true);
    datadogRum.addAction(DATADOG_EVENTS.API.TOGGLE_COAPPLICANT_API_CALL);
    try {
      const applyTradeIn = Boolean(tradeIn?.data?.applyTradeIn);

      const response = await httpWithAuth.post<
        Result<{
          creditApplication: Result<CreditApplication | null>;
          pricingAggregates?: Result<PricingAggregates>;
        }>
      >(
        toggleFinancingCoApplicant(applyTradeIn, {
          currentPageUrl: window.location.href
        }),
        {},
        {
          headers: {
            'x-client-id': FinancingApiClientId,
            'x-correlation-id': eventCorrelationId
          }
        }
      );

      if (!isSuccess(response.data)) {
        datadogRum.addAction(DATADOG_EVENTS.API.TOGGLE_COAPPLICANT_API_ERROR, {
          error: response.data.errorMessage
        });

        throw new Error(response.data.errorMessage);
      }

      const { creditApplication, pricingAggregates } = response.data.content;

      // CoSigner Error
      if (!isSuccess(creditApplication)) {
        datadogRum.addAction(DATADOG_EVENTS.API.TOGGLE_COAPPLICANT_API_ERROR, {
          error: creditApplication.errorMessage
        });

        setCoSigner(prevData => ({
          ...prevData,
          data: null,
          error: creditApplication.errorMessage
        }));
      }
      // No Error
      else if (creditApplication.content && isSuccess(pricingAggregates)) {
        setRefreshedData(
          'Toggle Co-Signer',
          applyTradeIn,
          creditApplication.content,
          pricingAggregates.content
        );
      }
    } catch (err) {
      datadogRum.addAction(DATADOG_EVENTS.API.TOGGLE_COAPPLICANT_API_ERROR, {
        error: err
      });
      logError(
        new ErrorWithContext(err, {
          message: 'Failed to toggle co-signer.'
        })
      );

      showApiError();

      // throw the error again so the consumer can handle it
      throw toError(err);
    } finally {
      setIsLoading(false);
    }
  }, [
    eventCorrelationId,
    setCoSigner,
    setIsLoading,
    setRefreshedData,
    tradeIn?.data?.applyTradeIn
  ]);
}
